import { faSearch } from '@fortawesome/pro-duotone-svg-icons';
import { GridApi } from 'ag-grid-community';
import { groupBy, keyBy } from 'lodash';
import { Moment } from 'moment';
import naturalCompare from 'natural-compare-lite';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { User } from 'api/models';
import TimeOffTriangle from 'assets/svgs/timeoff-triangle.svg';
import { Button } from 'components/atoms/button';
import {
  EmptyState,
  EmptyStateContainer as EmptyStateContainerAtom,
  EmptyStateIcon,
} from 'components/atoms/empty-state';
import { Grid } from 'components/atoms/grid';
import { TableSkeleton } from 'components/atoms/table';
import {
  getColumnClassesForTimeCell,
  getDays as getSevenDays,
} from 'components/organisms/timesheets/common/utils';
import { WorkingDaysConfiguration } from 'components/organisms/user-capacity';
import { useProjectsQuery } from 'queries';
import { fullName } from 'utils/full-name';
import { TimesheetStatus, UserLevelSummary } from '../types';
import { ActionCell, LabelWrapper } from './all-user-view-cells/action-cell';
import { CustomHeaderRenderer } from './all-user-view-cells/custom-header';
import { DayTimeEntryRenderer } from './all-user-view-cells/day-time-entry';
import { ProjectNameRenderer } from './all-user-view-cells/project-name';
import { TeamMemberRenderer } from './all-user-view-cells/team-member';
import { TotalTimeRenderer } from './all-user-view-cells/total-time';

const ProjectWiseTable = styled(Grid)`
  --ag-odd-row-background-color: var(--white-100);
  &&& .ag-row {
    border-bottom: 1px solid var(--gray-20);
  }

  .ag-header {
    height: 48px !important;
    min-height: 48px !important;
  }

  .ag-header-row {
    height: 100% !important;
  }

  .ag-react-container {
    color: rgba(0, 0, 0, 0.85);
  }

  ${LabelWrapper}
  ${Button} {
    display: none;
  }

  .ag-row > .timeoff-cell::after {
    content: '';
    background-image: url(${TimeOffTriangle});
    background-repeat: repeat;
    opacity: 0.15;
    position: absolute;
    inset: 0;
  }

  .ag-react-container {
    width: 100%;
  }

  &&& .ag-overlay-no-rows-wrapper {
    display: block;
  }
  .ag-pinned-right-header,
  .ag-cell.ag-cell-first-right-pinned:not(.ag-cell-range-left):not(.ag-cell-range-single-cell) {
    border-left: 0px;
  }

  .ag-row > .holiday-cell {
    opacity: 0.7;
  }

  .ag-row > .holiday-cell {
    background: var(--coolest-gray-30);
    background: repeating-linear-gradient(
      -47deg,
      #fff 1%,
      #fff 2%,
      var(--coolest-gray-30) 3%,
      #fff 4%,
      #fff 5%
    );
  }

  .ag-row > .holiday-cell:hover {
    background: var(--gray-30);
    background: repeating-linear-gradient(
      -47deg,
      #fff 1%,
      #fff 2%,
      var(--gray-30) 3%,
      #fff 4%,
      #fff 5%
    );
  }

  .ag-row > .today-cell {
    background: var(--gray-10);
  }

  .ag-row > .holiday-cell.today-cell {
    background: var(--coolest-gray-30);
    background: repeating-linear-gradient(
      -47deg,
      var(--gray-10) 1%,
      var(--gray-10) 2%,
      var(--coolest-gray-30) 3%,
      var(--gray-10) 4%,
      var(--gray-10) 5%
    );
  }
`;

const EmptyStateContainer = styled(EmptyStateContainerAtom)`
  && {
    text-align: left;
  }
`;

export const NoResponsesEmptyState = ({
  isManagerApproval,
}: {
  isManagerApproval: boolean;
}) => (
  <EmptyStateContainer>
    <EmptyState
      image={<EmptyStateIcon icon={faSearch} />}
      title={
        <FormattedMessage
          id={
            isManagerApproval
              ? 'timesheets.approval.manager.empty_state.title'
              : 'filter.no_results'
          }
        />
      }
      message={
        <FormattedMessage
          id={
            isManagerApproval
              ? 'timesheets.approval.manager.empty_state.message'
              : 'timesheets.approval.po.empty_state.message'
          }
        />
      }
      secondaryCTA={null}
    />
  </EmptyStateContainer>
);

interface Props {
  startDate: Moment;
  workingDays: WorkingDaysConfiguration;
  userTimesheets: UserLevelSummary[];
  onUserSelection: (userId: number, projectId?: number) => void;
  approveTimesheet: (
    userId: number,
    updatedAt: number,
    projectId?: number,
  ) => void;
  sendReminder: (user: User) => void;
  refreshData: () => void;
  searchValue: string;
  isWeekFreezed: boolean;
  isLoading: boolean;
  isManagerApproval: boolean;
  onGridReady: (params: any) => void;
  gridApi: GridApi | null;
  isUserAdmin: boolean;
}
export const AllUsersTable: FC<Props> = ({
  startDate,
  userTimesheets,
  workingDays,
  onUserSelection,
  approveTimesheet,
  sendReminder,
  refreshData,
  searchValue,
  isWeekFreezed,
  isLoading,
  isManagerApproval,
  onGridReady,
  gridApi,
  isUserAdmin,
}) => {
  const onFirstDataRendered = useCallback(
    () => gridApi?.sizeColumnsToFit(),
    [gridApi],
  );

  const { data: projects } = useProjectsQuery();

  const projectsMap = useMemo(() => {
    return keyBy(projects, 'projectId');
  }, [projects]);

  const columns = useMemo(() => {
    const rowDataGetter = function (params) {
      return params.data;
    };
    const dayColumns = getSevenDays(startDate).map((day, index) => {
      return {
        headerName: 'dateCol',
        cellRenderer: 'DayTimeEntryRenderer',
        cellRendererParams: {
          onUserSelection,
          sendReminder,
          day,
          workingDays,
          isManagerApproval,
        },
        headerComponentParams: {
          day,
        },
        cellClass: (params) =>
          params.value.status !== TimesheetStatus.NotSubmitted
            ? getColumnClassesForTimeCell(
                day,
                workingDays,
                '',
                params.value.user,
              )
            : '',

        valueGetter: rowDataGetter,
        colSpan: (params) => {
          if (params.data.status === TimesheetStatus.NotSubmitted) {
            return index === 0 ? 8 : 1;
          }

          return 1;
        },
        minwidth: 100,
        maxWidth: 180,
      };
    });

    const mangerFlowColumns = [
      {
        valueGetter: rowDataGetter,
        headerName: isManagerApproval
          ? `Team Members ${
              userTimesheets.length > 0 ? `(${userTimesheets.length})` : ''
            }`
          : 'Projects & Team members',
        cellRenderer: 'TeamMemberRenderer',
        cellRendererParams: {
          onUserSelection,
          suppressCount: true,
          isManagerApproval,
        },
        minWidth: 360,
        maxWidth: 880,
        cellStyle: { borderRight: '1px solid var(--coolest-gray-20)' },
      },
      ...dayColumns,
      {
        valueGetter: rowDataGetter,
        headerName: 'Total Time',
        cellRenderer: 'TotalTimeRenderer',
        cellRendererParams: {
          onUserSelection,
        },
        minWidth: 120,
        maxWidth: 200,
      },
      {
        valueGetter: rowDataGetter,
        cellRenderer: 'ActionCell',
        cellRendererParams: {
          approveTimesheet,
          isManagerApproval,
          onUserSelection,
          isWeekFreezed,
          refreshData,
          isUserAdmin,
        },
        minWidth: 120,
        maxWidth: 200,
      },
    ];

    return isManagerApproval
      ? mangerFlowColumns
      : [
          {
            field: 'projectId',
            headerName: 'Project Name',
            cellRenderer: 'ProjectNameRenderer',
            rowGroup: true,
            hide: true,
          },
          ...mangerFlowColumns,
        ];
  }, [
    approveTimesheet,
    isManagerApproval,
    isUserAdmin,
    isWeekFreezed,
    onUserSelection,
    refreshData,
    sendReminder,
    startDate,
    userTimesheets.length,
    workingDays,
  ]);

  const ordered = (timesheets) => {
    return (timesheets ?? []).sort((timesheetA, timesheetB) =>
      naturalCompare(fullName(timesheetA.user), fullName(timesheetB.user)),
    );
  };
  const grouped = groupBy(userTimesheets, 'status');

  const orderedUserTimesheets = [
    ...ordered(grouped[TimesheetStatus.Submitted]),
    ...ordered(grouped[TimesheetStatus.Rejected]),
    ...ordered(grouped[TimesheetStatus.Approved]),
    ...ordered(grouped[TimesheetStatus.NotSubmitted]),
  ];

  useEffect(() => {
    if (gridApi) {
      gridApi.setFilterModel({
        'ag-Grid-AutoColumn': {
          filterType: 'text',
          type: 'contains',
          filter: searchValue,
        },
      });
    }
  }, [gridApi, searchValue]);

  if (isLoading) {
    return <TableSkeleton columnCount={8} />;
  }

  return (
    <ProjectWiseTable
      className="ag-theme-balham"
      frameworkComponents={{
        emptyState: NoResponsesEmptyState,
        ProjectNameRenderer,
        TeamMemberRenderer,
        TotalTimeRenderer,
        DayTimeEntryRenderer,
        ActionCell,
        agColumnHeader: CustomHeaderRenderer,
      }}
      onGridReady={onGridReady}
      onFirstDataRendered={onFirstDataRendered}
      groupRowRendererParams={{
        innerRenderer: 'ProjectNameRenderer',
        projectsMap,
        userTimesheets,
        isUserAdmin,
        isWeekFreezed,
      }}
      suppressCellSelection
      autoGroupColumnDef={{
        headerName: 'Project Name',
      }}
      suppressContextMenu
      suppressMovableColumns
      applyColumnDefOrder
      groupDefaultExpanded={1}
      getRowNodeId={(d: UserLevelSummary) => `${d.userId}_${d.projectId}`}
      rowData={isManagerApproval ? orderedUserTimesheets : userTimesheets}
      size="large"
      columnDefs={columns}
      groupUseEntireRow
      animateRows
      defaultColDef={{
        editable: false,
        lockPinned: true,
        flex: 1,
        resizable: false,
      }}
      noRowsOverlayComponentFramework={NoResponsesEmptyState}
      noRowsOverlayComponentParams={{ isManagerApproval }}
    />
  );
};
